/**
 * Blog editor document actions — save the draft, and publish to the CMS.
 *
 * TWO commands with different blast radii, deliberately not one. `ed_save_draft`
 * persists the buffer to our `blog_content` row and never reaches WordPress
 * (`writes-config`). `ed_publish_cms` pushes the article LIVE to the client's
 * public site (`writes-live-site`). A single "save" that could do either is
 * exactly how "just save it" publishes a client's article, so the names carry
 * the difference.
 */
import { z } from 'zod';
import {
  defineCommand,
  refuse,
  type CommitAuthorization,
  type Diff,
  type Receipt,
  type Refusal,
  type ResolveInput,
  type TenantId,
  type WriteDeps,
} from '../contract';

const noArgs = z.object({});
type NoArgs = z.infer<typeof noArgs>;

interface DocTarget {
  readonly contentId: string;
}

async function resolveDoc(input: ResolveInput<NoArgs>): Promise<DocTarget | Refusal> {
  const { tenant, read } = input;
  if (!read.editorContext) {
    return refuse('This surface cannot see the blog editor.');
  }
  const ctx = read.editorContext(tenant);
  if (!ctx) {
    return refuse('No blog editor is open in this tab.', { retryable: true });
  }
  return { contentId: ctx.contentId };
}

export const edSaveDraft = defineCommand<NoArgs, DocTarget>({
  name: 'ed_save_draft',
  section: 'editor',
  consequence: 'writes-config',
  description:
    'Save the open blog article draft, exactly like the toolbar Save button. The content stays in Core and does NOT reach the client WordPress site. To publish, use ed_publish_cms.',
  args: noArgs,
  resolve: resolveDoc,
  diff(target: DocTarget, tenant: TenantId): Diff {
    return {
      table: 'blog_content',
      rowId: target.contentId,
      tenant,
      // `SEAM_BINDINGS.editorSaveDraft` reproduces `{ save: 'draft' }`.
      changes: [{ field: 'save', from: null, to: 'draft', authorability: 'exact' }],
      reachesGeneratedContent: false,
    };
  },
  async run(target: DocTarget, auth: CommitAuthorization, write: WriteDeps): Promise<Receipt | Refusal> {
    if (!write.editorSaveDraft) {
      return refuse('This surface cannot save the draft.');
    }
    return write.editorSaveDraft(auth);
  },
});

export const edPublishCms = defineCommand<NoArgs, DocTarget>({
  name: 'ed_publish_cms',
  section: 'editor',
  consequence: 'writes-live-site',
  description:
    'Publish the open blog article LIVE to the client WordPress site — it becomes public immediately. If the article has no WordPress post yet, this CREATES A NEW PUBLIC POST. This is the CMS Publish button and runs the same pre-publish checks. To save without publishing, use ed_save_draft.',
  args: noArgs,
  resolve: resolveDoc,
  diff(target: DocTarget, tenant: TenantId): Diff {
    return {
      table: 'blog_content',
      rowId: target.contentId,
      tenant,
      // `SEAM_BINDINGS.editorPublishCms` reproduces `{ publish: 'live' }`.
      changes: [{ field: 'publish', from: null, to: 'live', authorability: 'exact' }],
      reachesGeneratedContent: false,
    };
  },
  async run(target: DocTarget, auth: CommitAuthorization, write: WriteDeps): Promise<Receipt | Refusal> {
    if (!write.editorPublishCms) {
      return refuse('This surface cannot publish to WordPress.');
    }
    return write.editorPublishCms(auth);
  },
});
